import { useState, useCallback } from 'react';
import { CircuitSchema, SimulationResult, RecommendationResult } from '../types';
import { apiService } from '../services/apiService';

export interface UseSimulationReturn {
  results: SimulationResult[];
  recommendations: RecommendationResult[];
  selectedScenarioId: string | null;
  loading: boolean;
  recommendLoading: boolean;
  error: string | null;
  simulate: (schema: CircuitSchema) => Promise<void>;
  recommend: (schema: CircuitSchema) => Promise<void>;
  selectScenario: (scenarioId: string) => void;
  clearResults: () => void;
  activeResult: SimulationResult | null;
}

export function useSimulation(): UseSimulationReturn {
  const [results, setResults] = useState<SimulationResult[]>([]);
  const [recommendations, setRecommendations] = useState<RecommendationResult[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const simulate = useCallback(async (schema: CircuitSchema) => {
    setLoading(true);
    setError(null);
    setRecommendations([]);
    try {
      const data = await apiService.simulate(schema);
      setResults(data);
      if (data.length > 0) {
        setSelectedScenarioId(data[0].scenario_id);
      }
    } catch (err: any) {
      setError(err.message || 'Simulation failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const recommend = useCallback(async (schema: CircuitSchema) => {
    setRecommendLoading(true);
    setError(null);
    try {
      const data = await apiService.recommend(schema);
      setRecommendations(data);
    } catch (err: any) {
      setError(err.message || 'Recommendation failed');
      setRecommendations([]);
    } finally {
      setRecommendLoading(false);
    }
  }, []);

  const selectScenario = useCallback((scenarioId: string) => {
    setSelectedScenarioId(scenarioId);
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
    setRecommendations([]);
    setSelectedScenarioId(null);
    setError(null);
  }, []);

  const activeResult = results.find(r => r.scenario_id === selectedScenarioId) || null;

  return {
    results,
    recommendations,
    selectedScenarioId,
    loading,
    recommendLoading,
    error,
    simulate,
    recommend,
    selectScenario,
    clearResults,
    activeResult,
  };
}
